import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { resolveAssetUrl } from "@/contexts/SiteContentContext";
import { Loader2, Upload, X } from "lucide-react";

/**
 * Reusable image uploader.
 * Props:
 *  - value: current image url (string)
 *  - onChange(url): called after a successful upload or clear
 *  - label: optional label
 *  - aspect: tailwind aspect class (default 16/10)
 *  - testId: data-testid prefix
 */
const ImageUploader = ({ value, onChange, label = "Bild", aspect = "aspect-[16/10]", testId = "image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Bild hochgeladen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
      )}
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative ${aspect} rounded-2xl border-2 ${
          dragActive
            ? "border-sky-500 bg-sky-50 ring-4 ring-sky-200/60"
            : "border-dashed border-neutral-200 bg-neutral-50"
        } overflow-hidden group transition-all`}
        data-testid={`${testId}-uploader`}
      >
        {value ? (
          <>
            <img
              src={resolveAssetUrl(value)}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
            />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testId}-clear`}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
            {dragActive && (
              <div className="absolute inset-0 grid place-items-center bg-sky-500/30 backdrop-blur-sm pointer-events-none">
                <div className="rounded-full bg-white px-5 py-2 text-sm font-medium text-sky-700 shadow-lg">
                  Datei loslassen zum Ersetzen
                </div>
              </div>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-pick`}
            className="absolute inset-0 grid place-items-center text-neutral-500 hover:bg-neutral-100/60 transition-colors"
          >
            <div className="flex flex-col items-center gap-2 pointer-events-none">
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className={`w-6 h-6 ${dragActive ? "text-sky-600" : ""}`} />
              )}
              <div className="text-sm font-medium">
                {uploading
                  ? "Wird hochgeladen…"
                  : dragActive
                  ? "Datei loslassen zum Hochladen"
                  : "Klicken oder Datei hierher ziehen"}
              </div>
              <div className="text-xs text-neutral-400">PNG, JPG, WEBP · max. 8 MB</div>
            </div>
          </button>
        )}
      </div>

      <div className="flex gap-2 items-center">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        <input
          type="text"
          placeholder="… oder URL einfügen"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-url`}
          className="flex-1 h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
        />
        {value && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="h-10 rounded-xl bg-neutral-100 hover:bg-neutral-200 px-3 text-sm"
            data-testid={`${testId}-replace`}
          >
            Ersetzen
          </button>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
